import { useEffect, useRef } from 'react'
import { Bottone, Modale } from '../../componenti/base.tsx'
import '../../stile-posta-elenco.css'

/**
 * Scorciatoie da tastiera della webmail (541; come in Roundcube). Mai mentre si scrive in un campo, mai con
 * una finestra o un menu aperti sopra la pagina; impersonando (524) quelle che cambiano qualcosa o scrivono
 * (Canc, R, A, F) non fanno niente. «?» apre l'elenco.
 */

export type AzioniTastiera = {
  /** Canc: le scelte o la conversazione aperta nel cestino. */
  cestino: () => void
  rispondi: () => void
  rispondiATutti: () => void
  inoltra: () => void
  /** Esc: chiude la conversazione aperta, o toglie la selezione. */
  chiudi: () => void
  /** Ctrl/Cmd+A: sceglie tutte le righe caricate. */
  selezionaTutte: () => void
  aiuto: () => void
}

export const SCORCIATOIE: [string, string][] = [
  ['↓ ↑', 'Riga successiva e precedente dell’elenco'],
  ['Invio', 'Apre la conversazione della riga'],
  ['Esc', 'Chiude la conversazione aperta; poi toglie la selezione'],
  ['Canc', 'Sposta nel cestino le conversazioni scelte, o quella aperta (anche ⌫ sul Mac)'],
  ['R', 'Rispondi'],
  ['A', 'Rispondi a tutti'],
  ['F', 'Inoltra'],
  ['Ctrl+A o ⌘A', 'Sceglie tutte le righe dell’elenco'],
  ['?', 'Questo elenco'],
]

/** Si sta scrivendo, o c'e' qualcosa aperto sopra la pagina: le scorciatoie tacciono. */
function occupato(e: KeyboardEvent): boolean {
  const t = e.target as HTMLElement | null
  if (t && (t.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(t.tagName))) return true
  return !!document.querySelector('.velo, .segni-menu, .selettore-caselle-elenco')
}

/** Le righe dell'elenco, per le frecce. */
function righe(): HTMLElement[] {
  return [...document.querySelectorAll<HTMLElement>('.webmail .posta-scorrimento .posta-voce')]
}

export function useScorciatoie(attive: boolean, solaLettura: boolean, azioni: AzioniTastiera) {
  const corrente = useRef(azioni)
  corrente.current = azioni
  useEffect(() => {
    if (!attive) return
    const tasto = (e: KeyboardEvent) => {
      if (e.defaultPrevented || occupato(e)) return
      const a = corrente.current
      const mod = e.ctrlKey || e.metaKey
      if (mod && !e.altKey && e.key.toLowerCase() === 'a') { e.preventDefault(); a.selezionaTutte(); return }
      if (mod || e.altKey) return
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        const tutte = righe()
        if (!tutte.length) return
        e.preventDefault()
        const qui = tutte.indexOf(document.activeElement as HTMLElement)
        const partenza = qui >= 0 ? qui : tutte.findIndex((r) => r.classList.contains('attiva'))
        const dove = partenza < 0 ? 0 : Math.min(tutte.length - 1, Math.max(0, partenza + (e.key === 'ArrowDown' ? 1 : -1)))
        tutte[dove]!.focus()
        tutte[dove]!.scrollIntoView({ block: 'nearest' })
        return
      }
      // Invio su una riga la apre da solo (e' un bottone); fuori dalle righe apre quella evidenziata.
      if (e.key === 'Enter') {
        if ((document.activeElement as HTMLElement | null)?.classList.contains('posta-voce')) return
        const evidenziata = righe().find((r) => r.classList.contains('attiva'))
        if (evidenziata) { e.preventDefault(); evidenziata.click() }
        return
      }
      if (e.key === 'Escape') { a.chiudi(); return }
      if (e.key === '?') { e.preventDefault(); a.aiuto(); return }
      if (solaLettura) return
      if (e.key === 'Delete' || e.key === 'Backspace') { e.preventDefault(); a.cestino(); return }
      if (e.shiftKey) return
      const k = e.key.toLowerCase()
      if (k === 'r') { e.preventDefault(); a.rispondi() } else if (k === 'a') { e.preventDefault(); a.rispondiATutti() } else if (k === 'f') { e.preventDefault(); a.inoltra() }
    }
    document.addEventListener('keydown', tasto)
    return () => document.removeEventListener('keydown', tasto)
  }, [attive, solaLettura])
}

export function AiutoScorciatoie({ chiudi, solaLettura }: { chiudi: () => void; solaLettura: boolean }) {
  return (
    <Modale titolo="Scorciatoie da tastiera" chiudi={chiudi} fuocoIniziale="azioni"
            azioni={<Bottone onClick={chiudi}>Chiudi</Bottone>}>
      <dl className="posta-scorciatoie">
        {SCORCIATOIE.map(([tasti, cosa]) => (
          <div key={tasti}><dt><kbd>{tasti}</kbd></dt><dd>{cosa}</dd></div>
        ))}
      </dl>
      <p className="posta-nota">
        Non valgono mentre scrivi in un campo o con una finestra aperta.
        {solaLettura ? ' Stai vedendo il gestionale come un altro utente: Canc, R, A e F non fanno niente.' : ''}
      </p>
    </Modale>
  )
}
